<template>
  <div class="search" :class="{ focused }">
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" v-bind:svg-inline="''" v-bind:class="'icon'" v-bind:role="'presentation'" v-bind:focusable="'false'" v-bind:tabindex="'-1'"><path fill-rule="evenodd" clip-rule="evenodd" d="M14.057 12.881a6.668 6.668 0 10-1.176 1.176.827.827 0 00.035.038l3.536 3.536a.833.833 0 001.178-1.179l-3.535-3.536a.827.827 0 00-.038-.035zm-1.73-7.625a5 5 0 11-7.071 7.071 5 5 0 017.071-7.07z" class="path" fill="red"/></svg>

    <input
        ref="searchInput"
        v-model="query"
        type="text"
        :placeholder="$t('interface.search')"
        @focus="focus"
        @blur="blur"
        @keyup.enter="resultDetails"
    />

    <template v-if="isAuth">
      <div class="type">
        <select
            v-model="computedType"
            @change="search"
        >
          <option :value="-1">
            {{ $t('search.in_stock') }}
          </option>

          <option :value="0">
            {{ $t('search.available') }}
          </option>

          <option :value="1">
            {{ $t('search.all') }}
          </option>
        </select>

        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" v-bind:svg-inline="''" v-bind:class="'chevron'" v-bind:role="'presentation'" v-bind:focusable="'false'" v-bind:tabindex="'-1'"><path d="M4.229 5.172l-.943.942L8 10.828l4.714-4.714-.943-.942-3.77 3.77-3.772-3.77z" fill="red" class="path"/></svg>
      </div>
    </template>

    <transition name="fade-fast">
      <div
          v-if="query.length > 2 && focused"
          class="suggestions-wrapper"
      >
        <template v-if="suggestions.length">
          <ul>
            <li
                v-for="suggestion in suggestions"
                :key="suggestion.id"
            >
              <a @click="productDetail(suggestion.id)">
                <span class="name">
                  {{ suggestion.name }}
                </span>

                <span class="category">
                  {{ suggestion.category }}
                </span>
              </a>
            </li>
          </ul>

          <div class="separator"></div>
        </template>

        <router-link
            :to="{
            name: 'search',
            params: {
              query,
              type: computedType
            }
          }"
        >
          {{ $t('search.all_results', [query]) }}
        </router-link>
      </div>
    </transition>
  </div>
</template>

<script>
import { debounce } from '@/utils'
import { mapGetters, mapState } from 'vuex'
import axios from 'axios'

const QR8_BASE = 'https://qr8.uz/'

export default {
  name: 'Search',

  data() {
    return {
      query: '',
      focused: false,
      loading: false,

      // scanner
      scannerBuffer: '',
      scannerTimeout: null
    }
  },

  computed: {
    ...mapGetters('Misc', ['getCategoryById']),
    ...mapGetters('User', ['isAuth']),

    ...mapState('Products', ['searchSuggestions']),
    ...mapState('Misc', ['productFilterType']),

    computedType: {
      get() {
        return this.productFilterType
      },

      set(val) {
        this.$store.commit(
            'Misc/SET_PRODUCT_FILTER_TYPE',
            val
        )
      }
    },

    suggestions() {
      return this.searchSuggestions.map(item => ({
        ...item,

        category: item.categories?.[0]
            ? this.getCategoryById(
                item.categories[0]
            )?.[`name${this.$t('k')}`]
            : ''
      }))
    }
  },

  watch: {
    query: debounce(function () {
      this.search()
    }, 300)
  },

  mounted() {
    window.addEventListener('keydown', this.handleScanner)
  },

  beforeDestroy() {
    window.removeEventListener('keydown', this.handleScanner)
  },

  methods: {
    focus() {
      this.focused = true
    },

    blur() {
      setTimeout(() => {
        this.focused = false
      }, 200)
    },

    async search() {
      if (this.query.length < 3) return

      try {
        this.loading = true

        const params = {
          query: this.query,
          limit: 5
        }

        if (this.isAuth) {
          params.type = this.computedType
        }

        await this.$store.dispatch(
            'Products/loadSearchSuggestions',
            params
        )

      } catch (err) {
        console.error('SEARCH ERROR:', err)
      } finally {
        this.loading = false
      }
    },

    productDetail(id) {
      const currentLocation = location.href.split('#')[0]

      history.pushState({}, '', `${currentLocation}#${id}`)

      this.$store.dispatch('Products/setDetail', id)
    },

    resultDetails(e) {
      this.$router.push({
        name: 'search',
        params: {
          query: this.query,
          type: this.computedType
        }
      })

      e.target.blur()
      this.query = ''
    },

    // =====================================
    // QR / BARCODE SCANNER
    // =====================================

    async handleScanner(event) {
      if (event.key === 'Enter') {
        const scannedText = this.scannerBuffer.trim()
        this.scannerBuffer = ''

        if (!scannedText) return

        console.log('SCANNED TEXT:', scannedText)

        if (scannedText.startsWith(QR8_BASE)) {
          await this.handleQrCode(scannedText)
          return
        }

        // oddiy barcode
        this.query = scannedText
        await this.search()
        return
      }

      if (event.key.length === 1) {
        this.scannerBuffer += event.key
      }

      clearTimeout(this.scannerTimeout)
      this.scannerTimeout = setTimeout(() => {
        this.scannerBuffer = ''
      }, 150)
    },

    async handleQrCode(url) {
      try {
        this.loading = true

        // https://qr8.uz/p/xxx → https://qr8.uz/api/p/xxx
        const apiUrl = url.replace(QR8_BASE, `${QR8_BASE}api/`)

        console.log('API URL:', apiUrl)

        const { data } = await axios({
          method: 'post',
          url: apiUrl,
          data: {},
          headers: {
            'Content-Type': 'application/json',
            'Accept': '*/*'
          }
        })

        console.log('QR RESPONSE:', data)

        const name = data?.name || ''
        const codeMatch = name.match(/\(([^)]+)\)(?!.*\()/)
        const productCode = codeMatch?.[1]?.trim() || name

        if (!productCode) return

        this.query = productCode
        await this.search()

        const firstProduct = this.searchSuggestions?.[0]
        if (firstProduct?.id) {
          this.productDetail(firstProduct.id)
        }

      } catch (err) {
        console.error('QR ERROR:', err)
      } finally {
        this.loading = false
      }
    }
  }
}
</script>